import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { membershipPlans } from '../mockData';
import MembershipRegistrationDialog from '../components/MembershipRegistrationDialog';
import { Toaster } from '../components/ui/toaster';

const Membership = () => {
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('12');

  // Filtere Pakete basierend auf der ausgewählten Laufzeit
  const filteredPlans = membershipPlans.filter(plan => {
    if (selectedDuration === '1') {
      return plan.duration === '1 Monat';
    } else if (selectedDuration === '12') {
      return plan.duration === '12 Monate';
    } else if (selectedDuration === '24') {
      return plan.duration === '24 Monate';
    }
    return false;
  });

  return (
    <div className="bg-black min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBneW18ZW58MHx8fHwxNzYyODY5NDgyfDA&ixlib=rb-4.1.0&q=85"
            alt="Membership"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 uppercase">
            Mitgliedschaft bei POSITIV
          </h1>
          <p className="text-xl text-white/90">
            Wählen Sie die Mitgliedschaft, die zu Ihrem Lebensstil passt.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-black">
              Eine Mitgliedschaft. Unzählige Möglichkeiten.
            </h2>
            <p className="text-xl text-black/70 max-w-3xl mx-auto mb-8">
              Jede Mitgliedschaft bietet Zugang zu unseren erstklassigen Einrichtungen, exklusiven Kursen und einer Gemeinschaft von Gleichgesinnten.
            </p>

            {/* Duration Toggle */}
            <div className="flex justify-center items-center gap-2 flex-wrap">
              <button
                onClick={() => setSelectedDuration('1')}
                className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                  selectedDuration === '1'
                    ? 'bg-black text-white'
                    : 'bg-white text-black border-2 border-black/20 hover:border-black'
                }`}
              >
                1 Monat
              </button>
              <button
                onClick={() => setSelectedDuration('12')}
                className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                  selectedDuration === '12'
                    ? 'bg-black text-white'
                    : 'bg-white text-black border-2 border-black/20 hover:border-black'
                }`}
              >
                12 Monate
              </button>
              <button
                onClick={() => setSelectedDuration('24')}
                className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                  selectedDuration === '24'
                    ? 'bg-black text-white'
                    : 'bg-white text-black border-2 border-black/20 hover:border-black'
                }`}
              >
                24 Monate
              </button>
            </div>
          </div>

          {/* Membership Plans */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {filteredPlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`relative bg-white border-2 p-6 ${
                  plan.popular 
                    ? 'border-black shadow-2xl' 
                    : 'border-black/20'
                } hover:shadow-xl transition-shadow`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-1 text-xs font-bold uppercase">
                    BELIEBT
                  </div>
                )}
                {plan.isSpecial && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-1 text-xs font-bold uppercase shadow-lg">
                    SPECIAL TARIF
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-1 text-black">{plan.displayName || plan.name}</h3>
                  {plan.isSpecial && (
                    <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-2 inline-block">
                      <span className="text-xs font-semibold text-blue-700">🎓 Für Schüler, Studenten, Azubis & Rentner</span>
                    </div>
                  )}
                  <div className="text-sm font-medium text-black/60 mb-3">{plan.duration}</div>
                  {plan.summerPromo && (
                    <div className="bg-yellow-400 text-black text-center font-bold uppercase text-sm rounded-md px-3 py-2 mb-3">
                      Sommeraktion: 12 Wochen Gratis
                    </div>
                  )}
                  {plan.summerPromo ? (
                    <>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-4xl font-bold text-black">€0</span>
                        <span className="text-black/60 text-sm">/ {plan.period}</span>
                      </div>
                      <div className="text-xs text-black/60 mb-2">
                        Die ersten 12 Wochen gratis – ab Woche 13 nur €{plan.price} / {plan.period}.
                      </div>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-black">€{plan.price}</span>
                      <span className="text-black/60 text-sm">/ {plan.period}</span>
                    </div>
                  )}
                  {plan.setupFee && (
                    <div className="text-xs text-black/60 mb-3">
                      Aufnahmegebühr: €{plan.setupFee}
                    </div>
                  )}
                  {plan.description && (
                    <p className="text-xs text-black/70 leading-relaxed mb-3 line-clamp-3">{plan.description}</p>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                      <span className="text-black/80 text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full h-11 text-sm ${
                    plan.popular
                      ? 'bg-black text-white hover:bg-black/90'
                      : 'bg-transparent text-black border-2 border-black hover:bg-black hover:text-white'
                  }`}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setRegistrationDialogOpen(true);
                  }}
                >
                  Vertrag schließen
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-24">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
            Bereit anzufangen?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Besuchen Sie uns oder kontaktieren Sie uns für eine persönliche Beratung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
           
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-black hover:text-white h-12 px-8">
              Beratung anfordern
            </Button>
          </div>
        </div>
      </section>

      {/* Membership Registration Dialog */}
      <MembershipRegistrationDialog
        plan={selectedPlan}
        isOpen={registrationDialogOpen}
        onClose={() => setRegistrationDialogOpen(false)}
      />
      
      <Toaster />
    </div>
  );
};

export default Membership;
